import { globalConfig } from './shared/config/config';
import { getConfig, IConfig } from './url-reader';
import { IPostWithCTA } from './shared/types/Post';
import Logger from './shared/utils/logger';
import { ISocialFeedsGroupedItem, SocialFeedsTypesMap } from './shared/mappers/map-social-feeds';
import { IPersonalization } from './shared/types/Personalization';
import { ITintMeta } from './shared/services/tint.service';
import { IProduct, ITaggedProduct } from './shared/types/Product';
import { Nullable } from './shared/types/Typescript-utils';

interface IOpenPublicPostPostMessage {
  type: typeof globalConfig.postMessage.actions.openPublicPost;
  personalization: IPersonalization;
  socialFeeds: SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]>;
  config: IConfig;
  meta: ITintMeta;
}

interface IOpenReviewPostMessage {
  type: typeof globalConfig.postMessage.actions.openReview;
  personalization: IPersonalization;
  socialFeeds: SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]>;
  config: IConfig;
  meta: ITintMeta;
}

interface IOpenPopupPostMessage {
  type: typeof globalConfig.postMessage.actions.openPopup;
  index: number;
  posts: readonly IPostWithCTA[];
  personalization: IPersonalization;
  config: IConfig;
  meta: ITintMeta;
  socialFeeds: SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]>;
  customFontPopup?: Nullable<string>;
}

interface IThemeLoadPostMessage {
  type: typeof globalConfig.postMessage.actions.themeLoaded;
}

interface IOldThemeLoadPostMessage {
  type: typeof globalConfig.postMessage.actions.loadOldTheme;
}

interface IPublicPostSubmitPostMessage {
  type: typeof globalConfig.postMessage.actions.publicPostSubmitted;
  size: number;
}

interface IReviewSubmitPostMessage {
  type: typeof globalConfig.postMessage.actions.reviewSubmitted;
  size: number;
}

interface IClosePopupPostMessage {
  type: typeof globalConfig.postMessage.actions.closePopup;
  transition?: boolean;
  tintSlug: string;
}

interface IPopupLoadedPostMessage {
  type: typeof globalConfig.postMessage.actions.popupLoaded;
}

interface IPopupChangePostPostMessage {
  type: typeof globalConfig.postMessage.actions.popupPostChange;
  postId: string;
}

interface IPopupChangeUpdataPostData {
  type: typeof globalConfig.postMessage.actions.updatePopupData;
  taggedProducts: readonly ITaggedProduct[];
  products: readonly IProduct[];
}

interface IExpandPostMessage {
  type: typeof globalConfig.postMessage.actions.expandContainer;
  containerSize: number;
}

interface IScrollToTopMessage {
  type: typeof globalConfig.postMessage.actions.scrollToTopMessage;
}

interface IGenerateSchemaMessage {
  type: typeof globalConfig.postMessage.actions.generateSchema;
  schema: any; // the schema structure depends on theme
}

export type PostMessage =
  | IOpenPublicPostPostMessage
  | IOpenReviewPostMessage
  | IOpenPopupPostMessage
  | IThemeLoadPostMessage
  | IOldThemeLoadPostMessage
  | IPublicPostSubmitPostMessage
  | IReviewSubmitPostMessage
  | IClosePopupPostMessage
  | IPopupChangePostPostMessage
  | IPopupChangeUpdataPostData
  | IPopupLoadedPostMessage
  | IExpandPostMessage
  | IScrollToTopMessage
  | IGenerateSchemaMessage;

export class MessageService {
  public static send(obj: PostMessage): void {
    const config = getConfig();
    const data = 'tintSlug' in obj ? obj : { ...obj, tintSlug: config.tintSlug };
    try {
      // Send to parent if it is created by script, send to window if not
      if (config.innerIFrame) {
        window.postMessage(JSON.stringify(data), '*');
      } else {
        window.parent.postMessage(JSON.stringify(data), '*');
      }
    } catch (e) {
      Logger.error(e);
    }
  }
}
